<template>
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="false"
    :width="isMobile ? 260 : 550"
    location="bottom right"
  >
    <template #activator="{ props }">
      <v-text-field
        v-bind="props"
        :label="$t('dashboard.date-picker.label')"
        :model-value="formattedDate"
        :append-inner-icon="formattedDate && 'mdi-close'"
        :disabled="disabled"
        @click:append-inner.stop="clear"
        readonly
        prepend-inner-icon="mdi-calendar"
        class="ma-0 pa-0"
        density="compact"
        hide-details
      />
    </template>
    <v-card elevation="3">
      <v-card-text>
        <v-row class="date-picker__content">
          <v-col
            :cols="isMobile ? 12 : 4"
            class="pa-0 date-picker__content-dates"
          >
            <v-list @update:selected="onListItemSelect">
              <v-list-item
                v-for="{ title, value } in presetDates"
                :key="title"
                clickable
                density="compact"
                :title="title"
                :value="value"
                :active="value === selectedPreset"
                color="primary"
              /> </v-list
          ></v-col>
          <v-col
            v-if="!isMobile"
            cols="8"
          >
            <v-date-picker
              v-model="internalModelValue"
              width="100%"
              elevation="0"
              multiple="range"
              color="primary"
              show-adjacent-months
              hide-title
              hide-header
          /></v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="date-picker__actions">
        <v-spacer />
        <v-btn
          variant="text"
          @click="onCancel"
        >
          {{ $t('dashboard.date-picker.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          @click="onSave"
        >
          {{ $t('dashboard.date-picker.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>
<script lang="ts" setup>
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDate, useDisplay } from 'vuetify'

import { DATE_LONG, formatDate } from '@/lib/dateTime'
import { TrackingPlugin } from '@/plugins/tracking'

import {
  DashboardContext,
  dashboardContextDefault,
  DashboardContextSymbol,
} from './dashboardContext'

type PresetsValues =
  | 'today'
  | 'yesterday'
  | 'last-week'
  | 'last-7-days'
  | 'last-month'
  | 'last-30-days'
  | 'last-quarter'
  | 'last-90-days'
  | 'last-year'
  | 'last-360-days'
  | 'year-to-date'
type Presets = {
  title: string
  value: PresetsValues
  preset: Date[]
}

const dashboardContext = inject<DashboardContext>(DashboardContextSymbol, dashboardContextDefault)

withDefaults(
  defineProps<{
    disabled?: boolean
  }>(),
  {
    disabled: false,
  }
)

const { isSameDay, startOfDay, endOfDay, startOfMonth, endOfMonth, startOfYear } = useDate()
const { mdAndDown: isMobile } = useDisplay()
const { t } = useI18n()
const $tracking = inject<TrackingPlugin>('tracking')

const setStartOfDay = (date: Date): Date => startOfDay(date) as Date
const setEndOfDay = (date: Date): Date => endOfDay(date) as Date

const menuOpen = ref(false)
const selectedPreset = ref<PresetsValues | null>()
const internalModelValue = ref<Date[]>([])

const presetDates = computed<Presets[]>(() => {
  const today = setStartOfDay(new Date())

  return [
    {
      title: t('dashboard.date-picker.today'),
      value: 'today',
      preset: (() => {
        const now = new Date()
        return [setStartOfDay(today), setEndOfDay(now)]
      })(),
    },
    {
      title: t('dashboard.date-picker.yesterday'),
      value: 'yesterday',
      preset: (() => {
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        const yesterdayStart = setStartOfDay(yesterday)
        return [yesterdayStart, setEndOfDay(yesterdayStart)]
      })(),
    },
    // {
    //   title: 'Last week',
    //   value: 'last-week',
    //   preset: (() => {
    //     const currentWeekStart = startOfWeek(today, 'DE') as Date
    //     const lastWeekEnd = new Date(currentWeekStart)
    //     lastWeekEnd.setDate(lastWeekEnd.getDate() - 1)
    //     const lastWeekStart = startOfWeek(lastWeekEnd) as Date
    //     return [setStartOfDay(lastWeekStart), setStartOfDay(lastWeekEnd)]
    //   })(),
    // },
    {
      title: t('dashboard.date-picker.last7Days'),
      value: 'last-7-days',
      preset: (() => {
        const sevenDaysAgo = new Date()
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6)
        return [setStartOfDay(sevenDaysAgo), setEndOfDay(new Date())]
      })(),
    },
    {
      title: t('dashboard.date-picker.lastMonth'),
      value: 'last-month',
      preset: (() => {
        const lastMonth = new Date()
        lastMonth.setMonth(lastMonth.getMonth() - 1)
        const lastMonthStart = setStartOfDay(startOfMonth(lastMonth) as Date)
        const lastMonthEnd = endOfMonth(lastMonth) as Date
        return [lastMonthStart, setEndOfDay(lastMonthEnd)]
      })(),
    },
    {
      title: t('dashboard.date-picker.last30Days'),
      value: 'last-30-days',
      preset: (() => {
        const thirtyDaysAgo = new Date()
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29)
        return [setStartOfDay(thirtyDaysAgo), setEndOfDay(today)]
      })(),
    },
    {
      title: t('dashboard.date-picker.lastQuarter'),
      value: 'last-quarter',
      preset: (() => {
        const now = new Date()
        const currentMonth = new Date().getMonth()
        const currentQuarterStart = Math.floor(currentMonth / 3) * 3
        const lastQuarterStart = currentQuarterStart === 0 ? 9 : currentQuarterStart - 3
        const lastQuarterYear =
          currentQuarterStart === 0 ? now.getFullYear() - 1 : now.getFullYear()

        const quarterStartDate = new Date(lastQuarterYear, lastQuarterStart, 1)
        const quarterEndDate = new Date(lastQuarterYear, lastQuarterStart + 3, 0)

        return [setStartOfDay(quarterStartDate), setEndOfDay(quarterEndDate)]
      })(),
    },
    {
      title: t('dashboard.date-picker.last90Days'),
      value: 'last-90-days',
      preset: (() => {
        const ninetyDaysAgo = new Date()
        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 89)
        return [setStartOfDay(ninetyDaysAgo), setEndOfDay(today)]
      })(),
    },
    {
      title: t('dashboard.date-picker.lastYear'),
      value: 'last-year',
      preset: (() => {
        const lastYear = new Date()
        lastYear.setFullYear(lastYear.getFullYear() - 1)
        const lastYearStart = setStartOfDay(startOfYear(lastYear) as Date)
        const lastYearEnd = new Date(lastYear.getFullYear(), 11, 31)
        return [lastYearStart, setEndOfDay(lastYearEnd)]
      })(),
    },
    {
      title: t('dashboard.date-picker.last360Days'),
      value: 'last-360-days',
      preset: (() => {
        const threeSixtyDaysAgo = new Date()
        threeSixtyDaysAgo.setDate(threeSixtyDaysAgo.getDate() - 359)
        return [setStartOfDay(threeSixtyDaysAgo), setEndOfDay(today)]
      })(),
    },
    {
      title: t('dashboard.date-picker.year-to-date'),
      value: 'year-to-date',
      preset: (() => {
        const now = new Date()
        const yearStart = setStartOfDay(startOfYear(now) as Date)
        return [yearStart, setEndOfDay(today)]
      })(),
    },
  ]
})

const clear = () => {
  internalModelValue.value = []
  onSave()
}
const onCancel = () => {
  if (!dashboardContext.dateFilters.value.length) internalModelValue.value = []
  else {
    const [start, end] = dashboardContext.dateFilters.value
    internalModelValue.value = generateDateRange(start, end)
  }

  menuOpen.value = false
}

const formattedDate = computed(() => {
  if (internalModelValue.value.length === 0) return ''
  if (internalModelValue.value.length === 1) {
    return formatDate(internalModelValue.value[0], DATE_LONG)
  }
  const [start] = internalModelValue.value
  const end = internalModelValue.value[internalModelValue.value.length - 1]
  return `${formatDate(start, DATE_LONG)} - ${formatDate(end, DATE_LONG)}`
})

const onSave = () => {
  const getFirstAndLastDate = (dates: Date[]) => {
    if (dates.length === 0) return []
    return [dates[0], dates[dates.length - 1]]
  }
  dashboardContext.setDateFilters(getFirstAndLastDate(internalModelValue.value))
  menuOpen.value = false
}

const onListItemSelect = (value: unknown) => {
  const [preset = null] = value as PresetsValues[]
  if (!preset) {
    selectedPreset.value = null
    internalModelValue.value = []
    return
  }
  selectedPreset.value = preset
  setDateFromPreset()
}

const generateDateRange = (start: Date, end: Date): Date[] => {
  const dates: Date[] = []
  const current = new Date(start)
  if (isSameDay(start, end)) {
    return [start, end]
  }
  while (current <= end) {
    const isExactDay = isSameDay(current, end)
    if (isExactDay) {
      dates.push(end)
      break
    }
    dates.push(new Date(current))
    current.setDate(current.getDate() + 1)
  }

  return dates
}

const setDateFromPreset = () => {
  if (!selectedPreset.value) return
  const presetDate = presetDates.value.find((p) => p.value === selectedPreset.value)
  if (presetDate) {
    const [start, end] = presetDate.preset
    internalModelValue.value = generateDateRange(start, end)
    $tracking.event('Dashboard Date Picker', 'Selected', presetDate.value)
  }
}

watch(internalModelValue, (value) => {
  if (value.length === 0) {
    selectedPreset.value = null
  }

  const preset = presetDates.value.find((p) => {
    const [start, end] = p.preset
    if (value.length === 0) return false
    const selectedStart = value[0]
    const selectedEnd = value[value.length - 1]
    return isSameDay(start, selectedStart) && isSameDay(end, selectedEnd)
  })
  if (preset) selectedPreset.value = preset.value
  else selectedPreset.value = null
})

watch(menuOpen, (value) => {
  if (value) $tracking.event('Dashboard Date Picker', 'Opened')
  if (!value) onCancel()
})
</script>
<style scoped>
.date-picker__content {
  height: 350px;
  overflow: hidden;
}
.date-picker__content-dates {
  height: 100%;
  overflow: auto;
}
.date-picker__actions {
  background-color: white;
  border-top: 1px solid #e0e0e0;
}
</style>
